import { HfsSettingsActionEnum, HfsActionSetting } from 'fleet-data-model';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { AppDispatch } from '~/store/Store';
import type { AppState } from '~/reducers';
import { OperationStatus } from '~/domain/assets/models';
import { HfsOperationStateReflectionPayload } from '~/domain/assets/models/HfsOperationStateReflectionPayload';
import { patchRoboticMowerHfsSettingValue } from '~/api/robotic-mower-api/robotic-mower-api';

export enum UpdateStatus {
  Posting = 'POSTING',
  Rejected = 'REJECTED',
  Waiting = 'WAITING',
  Failed = 'FAILED',
  Success = 'SUCCESS',
}

export interface Update {
  assetId: string;
  operationId?: string;
  settingType: string;
  status: UpdateStatus;
  errorLangKey?: string;
  value?: string | number;
  enabled?: boolean;
  startTimestamp: number;
  failureTimeout: number;
  settingDataUpdates?: Record<string, unknown>;
  hfsActionId?: string;
}

export enum SettingTypeEnum {
  REQUIRE_PIN_AFTER_STOP = 'REQUIRE_PIN_AFTER_STOP',
  ALARM_DURATION = 'ALARM_DURATION',
  CARRIED_AWAY = 'CARRIED_AWAY',
  STOP_BUTTON_PRESSED = 'STOP_BUTTON_PRESSED',
  GEOFENCE = 'GEOFENCE',
  GEOFENCE_ENABLED = 'GEOFENCE_ENABLED',
  GEOFENCE_RADIUS = 'GEOFENCE_RADIUS',
  GEOFENCE_CENTRAL_POINT = 'GEOFENCE_CENTRAL_POINT',
}

export const initialState: SecuritySettingObjectType = {
  assetId: undefined,
  updates: [],
};

export type SecuritySettingType = {
  isLoadingInProgress: boolean;
  errorKey?: string;
  updateSuccessful?: boolean;
};

export interface Fields {
  assetId?: string;
  securitySettings: SecuritySettingObjectType;
}

type SecuritySettingObjectType = {
  assetId?: string;
  updates: Update[];
};

const reducer = createSlice({
  name: 'robotic-security-settings',
  initialState,
  reducers: {
    reset: (state) => {
      state.assetId = undefined;
    },
    init: (
      state,
      action: PayloadAction<{
        assetId: string;
      }>
    ) => {
      state.assetId = action.payload.assetId;
    },
    registerUpdate: (
      state,
      action: PayloadAction<
        {
          settingType: string;
          assetId: string;
          failureTimeout: number;
        } & Partial<Update>
      >
    ) => {
      const { settingType, assetId } = action.payload;
      const duplicate = state.updates.find(
        (update) => update.assetId === assetId && update.settingType === settingType
      );
      if (duplicate) {
        return;
      }
      state.updates.push({
        ...action.payload,
        status: UpdateStatus.Posting,
        startTimestamp: Date.now(),
      });
    },
    modifyUpdate: (
      state,
      action: PayloadAction<
        {
          settingType: string;
          assetId: string;
          operationId?: string;
        } & Partial<Update>
      >
    ) => {
      const { settingType, assetId } = action.payload;
      state.updates = state.updates.map((update) =>
        update.assetId === assetId && update.settingType === settingType
          ? { ...update, ...action.payload }
          : update
      );
    },

    clearUpdate: (
      state,
      action: PayloadAction<{
        settingType: string;
        assetId: string;
      }>
    ) => {
      const { settingType, assetId } = action.payload;
      state.updates = state.updates.filter(
        (update) => !(update.assetId === assetId && update.settingType === settingType)
      );
    },

    applyOperationStatusUpdate: (
      state,
      action: PayloadAction<{ operationId: string; status: OperationStatus }>
    ) => {
      const { operationId, status } = action.payload;

      if (status === OperationStatus.Success || status === OperationStatus.Sent) {
        state.updates = state.updates.map((update) =>
          update.operationId === operationId ? { ...update, status: UpdateStatus.Success } : update
        );
      }
      if (status === OperationStatus.Error || status === OperationStatus.Timeout) {
        state.updates = state.updates.map((update) =>
          update.operationId === operationId
            ? {
                ...update,
                status: UpdateStatus.Failed,
                errorLangKey: 'web.machine_details_v2.robotic_mower_settings.error',
              }
            : update
        );
      }
    },
  },
});

const { applyOperationStatusUpdate } = reducer.actions;
export const { reset, init, registerUpdate, modifyUpdate, clearUpdate } = reducer.actions;

export const reflectOperationStatusUpdate =
  (payload: HfsOperationStateReflectionPayload) =>
  (dispatch: AppDispatch, getState: () => AppState) => {
    const { operationId, status } = payload;
    const { updates } = getState().roboticSecuritySettings;
    dispatch(applyOperationStatusUpdate(payload));

    if (status === OperationStatus.Success || status === OperationStatus.Sent) {
      const successUpdate = updates.find((update) => update.operationId === operationId);
      if (successUpdate?.settingDataUpdates !== undefined) {
        dispatch(
          patchRoboticMowerHfsSettingValue({
            settingDataOverwrites: successUpdate.settingDataUpdates as unknown as HfsActionSetting,
            hfsActionId: successUpdate.hfsActionId as HfsSettingsActionEnum,
            assetId: successUpdate.assetId,
          })
        );
      }
    }
  };

export default reducer.reducer;
